import { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { InventoryItem } from './useInventory';

interface InventoryUsageHistory {
  id: string;
  itemId: string;
  eventId: string;
  eventName: string;
  eventDate: string;
  quantityUsed: number;
  userId: string;
  createdAt: string;
}

export const useInventoryItem = (itemId: string | undefined) => {
  const { user } = useAuth();
  const [item, setItem] = useState<InventoryItem | null>(null);
  const [usageHistory, setUsageHistory] = useState<InventoryUsageHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [itemExists, setItemExists] = useState<boolean>(true);

  const fetchItem = async () => {
    if (!itemId || !user?.uid) {
      setItem(null);
      setLoading(false);
      setItemExists(false);
      return;
    }

    try {
      const itemRef = doc(db, 'inventoryItems', itemId);
      const docSnapshot = await getDoc(itemRef);

      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        
        let createdAt = new Date().toISOString();
        let updatedAt = new Date().toISOString();
        
        if (data.createdAt?.toDate) {
          createdAt = data.createdAt.toDate().toISOString();
        }
        
        if (data.updatedAt?.toDate) {
          updatedAt = data.updatedAt.toDate().toISOString();
        }
        
        setItem({
          id: docSnapshot.id,
          itemName: data.itemName || '',
          description: data.description,
          photoUrl: data.photoUrl,
          photoUrl2: data.photoUrl2,
          quantity: data.quantity || 1,
          unitValue: data.unitValue,
          brand: data.brand,
          store: data.store,
          category: data.category,
          measurements: data.measurements,
          observations: data.observations,
          loan: data.loan || null,
          userId: data.userId,
          createdAt,
          updatedAt,
        } as any);
        setItemExists(true);
        setError(null);
      } else {
        setItem(null);
        setItemExists(false);
        setError(null);
      }
    } catch (err) {
      setError('Erro ao carregar item');
      setItemExists(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItem();
  }, [itemId, user?.uid]);

  useEffect(() => {
    if (!itemId || !user?.uid || !itemExists) {
      setUsageHistory([]);
      return;
    }

    const fetchHistory = async () => {
      try {
        const historyQuery = query(
          collection(db, 'inventoryUsageHistory'),
          where('itemId', '==', itemId),
          where('userId', '==', user.uid),
          orderBy('eventDate', 'desc')
        );

        const snapshot = await getDocs(historyQuery);
        const history: InventoryUsageHistory[] = [];
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          history.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
          } as InventoryUsageHistory);
        });

        setUsageHistory(history);
      } catch (err) {
        // Silently handle error
      }
    };

    fetchHistory();
  }, [itemId, user?.uid, itemExists]);

  return {
    item,
    usageHistory,
    loading,
    error,
    itemExists,
    refreshItem: fetchItem,
  };
};
